import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkMot } from '../../ressources/functionJS/checkMot';
import { glossaire_type_de_vue } from '../../ressources/glossaires/glossaire_type_de_vue';
import {
  reqRecupererAccueilSummary,
  reqVerifierMisesAJourAccueil,
} from '../../services/actions/accueil';
import * as actions from '../../services/actions';
import styles from './AccueilDashboard.module.scss';

// Importation des widgets
import {
  WidgetAlarmes,
  WidgetCommentCaMarche,
  WidgetContact,
  WidgetProjets,
  WidgetRapports,
  WidgetWidgetsLayout,
} from './widgets';

// Componente principal AccueilDashboard
const AccueilDashboard = () => {
  const dispatch = useDispatch();
  const prenom = useSelector((state) => state.authentifie.name);
  const summary = useSelector(
    (state) => state.accueil?.summary ?? state.accueil
  );
  const loading = useSelector((state) => state.accueil.loading);

  // Usar o state redux dos favoritos para garantir sincronização
  const favoriteGraphsList = useSelector(
    (state) => state.graphe.favorite_graphs_list || []
  );

  // Charger les données au montage
  useEffect(() => {
    dispatch(reqRecupererAccueilSummary());
    // Garantir que os favoritos sejam carregados também
    dispatch(actions.reqRecupererFavoriteGraphsV25());
  }, [dispatch]);

  // Polling toutes les 60 secondes pour détecter les mises à jour
  useEffect(() => {
    const intervalId = setInterval(() => {
      dispatch(reqVerifierMisesAJourAccueil());
    }, 60000);
    return () => clearInterval(intervalId);
  }, [dispatch]);

  // Extraire les données réelles avec fallback sur les données mock
  const isPlanView = (view) =>
    parseInt(view?.view_type, 10) === glossaire_type_de_vue.plan_et_carte;
  const isDashboardView = (view) =>
    parseInt(view?.view_type, 10) === glossaire_type_de_vue.tableau_de_bord;
  const {
    alarmesList,
    rapportsList,
    dashboardsList,
    graphesFavorisList,
    vuesFavoritesList,
    viewData,
    projectsListSummary,
    allProjectsSummary,
  } = useMemo(() => {
    const alarmes = Array.isArray(summary?.alarmes) ? summary.alarmes : [];
    const rapports = Array.isArray(summary?.rapports) ? summary.rapports : [];
    const views = Array.isArray(summary?.views) ? summary.views : [];
    const graphes = Array.isArray(summary?.graphs) ? summary.graphs : [];
    const projets = Array.isArray(summary?.projects)
      ? summary.projects.filter((project) => project.favori)
      : [];
    const allProjects = Array.isArray(summary?.projects)
      ? summary.projects
      : [];
    const favoriteViewsFromApi = Array.isArray(summary?.favorite_views)
      ? summary.favorite_views
      : [];
    const favoriteDashboardsFromApi = Array.isArray(
      summary?.favorite_dashboards
    )
      ? summary.favorite_dashboards
      : [];

    const favoriteViewIds = Array.isArray(summary?.favorite_views_list)
      ? summary.favorite_views_list.map((id) => parseInt(id, 10))
      : [];
    const favoriteDashboardIds = Array.isArray(
      summary?.favorite_dashboards_list
    )
      ? summary.favorite_dashboards_list.map((id) => parseInt(id, 10))
      : [];
    // Usar o state redux dos favoritos em vez do summary para garantir sincronização
    const favoriteGraphIds = Array.isArray(favoriteGraphsList)
      ? favoriteGraphsList.map((id) => parseInt(id, 10))
      : [];

    const hasFavoriteViews = favoriteViewIds.length > 0;
    const hasFavoriteDashboards = favoriteDashboardIds.length > 0;
    const hasFavoriteGraphs = favoriteGraphIds.length > 0;

    const planViewsFavoritesFromApi = favoriteViewsFromApi.filter(isPlanView);
    const planViewsFromFavoriteIds = hasFavoriteViews
      ? views.filter(
          (view) =>
            isPlanView(view) &&
            favoriteViewIds.includes(parseInt(view?.view_id, 10))
        )
      : [];
    const allPlanViewsFromSummary = views.filter(isPlanView);

    const vuesFavorites =
      planViewsFavoritesFromApi.length > 0
        ? planViewsFavoritesFromApi
        : planViewsFromFavoriteIds.length > 0
          ? planViewsFromFavoriteIds
          : allPlanViewsFromSummary;

    const dashboardsFavoritesFromViews = hasFavoriteDashboards
      ? views.filter(
          (view) =>
            isDashboardView(view) &&
            favoriteDashboardIds.includes(parseInt(view?.view_id, 10))
        )
      : [];

    const allDashboardsFromSummary = views.filter(isDashboardView);

    const dashboardsFavorites =
      favoriteDashboardsFromApi.filter(isDashboardView).length > 0
        ? favoriteDashboardsFromApi.filter(isDashboardView)
        : dashboardsFavoritesFromViews.length > 0
          ? dashboardsFavoritesFromViews
          : allDashboardsFromSummary.length > 0
            ? allDashboardsFromSummary
            : favoriteDashboardsFromApi.filter(
                (v) => v.view_type !== glossaire_type_de_vue.plan_et_carte
              );

    const graphesFavoris = hasFavoriteGraphs
      ? graphes.filter((graphe) =>
          favoriteGraphIds.includes(parseInt(graphe?.graph_id, 10))
        )
      : graphes;

    return {
      alarmesList: alarmes,
      rapportsList: rapports,
      dashboardsList: dashboardsFavorites,
      graphesFavorisList: graphesFavoris,
      vuesFavoritesList: vuesFavorites,
      viewData: summary?.viewData,
      projectsListSummary: projets,
      allProjectsSummary: allProjects,
    };
  }, [summary, favoriteGraphsList]);

  if (loading && !summary) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.bienvenue}>
          {checkMot('chargement_en_cours')}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.bienvenue}>
        {checkMot('bienvenue')} <b>{prenom}</b>{' '}
        {checkMot('sur_la_platforme_argos')}
      </div>
      <div className={styles.widgetsGrid}>
        <WidgetWidgetsLayout
          dashboards={dashboardsList}
          vuesFavorites={vuesFavoritesList}
          graphesFavoris={graphesFavorisList}
          viewData={viewData}
          loading={loading}
        />
        <div className={[styles.area, styles.projetsArea].join(' ')}>
          <WidgetProjets projects={projectsListSummary} loading={loading} />
        </div>
        <div className={[styles.area, styles.midArea].join(' ')}>
          <div className={styles.midGrid}>
            <div className={styles.midTop}>
              <WidgetAlarmes alarmes={alarmesList} loading={loading} />
            </div>
            <div className={styles.midBottom}>
              <WidgetRapports rapports={rapportsList} loading={loading} />
            </div>
          </div>
        </div>
        <div className={[styles.area, styles.rightArea].join(' ')}>
          <div className={styles.rightGrid}>
            <div className={styles.rightTop}>
              <WidgetContact projects={allProjectsSummary} loading={loading} />
            </div>
            <div className={styles.rightBottom}>
              <WidgetCommentCaMarche />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccueilDashboard;
